import { request } from "../../api";

import type { URLPoolKind, URLVulnStatus } from "./urlPool";

// Matches `apps.network.models.URLAccessVerdict` (backend/apps/network/models.py):
// ALLOWED = "allowed", DENIED = "denied", LOGGED = "logged". Do not reintroduce
// "blocked" here — the API never returns it and a `verdict=blocked` filter would
// silently match zero rows server-side.
export type URLAccessVerdict = "allowed" | "denied" | "logged";

export type BlockReasonCode =
  | "block_rule"
  | "vuln_autoblock"
  | "default_disallow"
  | "unknown";

export interface NetworkEventRow {
  id: string;
  device_id: string;
  device_uid: string;
  url_pool_id: string;
  url_pool_kind: URLPoolKind;
  url_pool_value: string;
  url_pool_vuln_status: URLVulnStatus;
  verdict: URLAccessVerdict;
  policy_version: number;
  occurred_at: string;
  policy_name?: string | null;
  policy_hash?: string | null;
  policy_default_action?: string | null;
  block_reason_code?: BlockReasonCode | null;
  block_reason_label?: string | null;
  block_reason_source?: string | null;
  has_violation_detail?: boolean;
}

export interface NetworkPolicySummary {
  version: number;
  hash: string;
  name?: string | null;
  policy_id?: string | null;
  default_action: string;
  allow_count: number;
  block_count: number;
}

export interface NetworkEventMatchedRule {
  list: "block" | "allow";
  kind: string;
  value: string;
  source?: string;
  severity?: string;
}

export interface NetworkEventBlockReason {
  code: BlockReasonCode;
  source: string;
  label: string;
}

export interface NetworkViolationDetail {
  id: string;
  attempted_at: string;
  destination: string;
  destination_type: string;
  resolved_ip: string;
  protocol: string;
  port: number | null;
  protection_policy_mode: string;
  matched_rule_id: string | null;
  policy_version: number;
  policy_hash: string;
  bytes_denied: number;
}

export interface NetworkEventAnalysis {
  block_reason: NetworkEventBlockReason | null;
  matched_rule: NetworkEventMatchedRule | null;
  policy: NetworkPolicySummary | null;
  violation: NetworkViolationDetail | null;
}

export interface NetworkEventDetail {
  event: NetworkEventRow;
  analysis: NetworkEventAnalysis;
  violation: NetworkViolationDetail | null;
}

/**
 * Query params accepted by `GET /api/admin/v1/monitoring/network-events/`
 * (backend/apps/api_admin/v1/monitoring/views.py `_network_events_queryset`
 * + `NetworkEventsListView`):
 *  - `device`      exact `Device.id` match
 *  - `device_uid`  icontains on `device.device_uid` (ignored when `q` is set)
 *  - `search`      icontains on `url_pool.value` (ignored when `q` is set)
 *  - `q`           icontains on `url_pool.value` OR `device.device_uid` —
 *                   takes priority over `device_uid`/`search`
 *  - `verdict`     exact match against `URLAccessVerdict`; unrecognized
 *                   values are silently ignored server-side (no filter applied)
 *  - `kind`        exact match against `URLPoolKind`; same silent-ignore rule
 *  - `from`/`to`   ISO datetimes, inclusive bounds on `occurred_at`
 *  - `limit`       clamped to 1-200, defaults to 50
 *  - `offset`      clamped to 0-10,000,000, defaults to 0
 * `device_group` and `platform` (the Insights workspace's shared scope
 * filters) are NOT accepted by this endpoint — only `from`/`to` from that
 * shared scope apply here.
 */
export interface NetworkEventsFilters {
  device?: string;
  device_uid?: string;
  search?: string;
  q?: string;
  verdict?: URLAccessVerdict;
  kind?: URLPoolKind;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface NetworkEventsPage {
  total: number;
  limit: number;
  offset: number;
  items: NetworkEventRow[];
}

function qs(params: object): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params) as [string, unknown][]) {
    if (v === undefined || v === "" || v === null) continue;
    sp.append(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const networkEventsApi = {
  list: (filters: NetworkEventsFilters = {}) =>
    request<NetworkEventsPage>(`/api/admin/v1/monitoring/network-events/${qs(filters)}`),

  get: (eventId: string) =>
    request<NetworkEventDetail>(`/api/admin/v1/monitoring/network-events/${eventId}/`),
};
